import React from 'react';
import { Card, Typography, Descriptions, Avatar, Empty, Spin, Tooltip } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getUser } from '@/api/users';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { formatDateTimeWithTooltip } from '@/utils/date';

const { Title } = Typography;

interface UserInfoTabProps {
  userId: string;
}

const UserInfoTab: React.FC<UserInfoTabProps> = ({ userId }) => {
  const { t } = useTranslation(['common']);
  const { firestoreUser } = useAuth();

  // Fetch the full user data using the userId
  const { data: user, isLoading, error } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUser(userId, firestoreUser!),
    enabled: !!userId && !!firestoreUser,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !user) {
    return (
      <Card>
        <Empty description={t('common:userNotFound', 'User not found')} />
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <Avatar 
          size={100} 
          src={user.profilePicture?.url}
        >
          {user.displayName?.[0] || 'U'}
        </Avatar>
      </div>
      <Title level={4}>{user.displayName}</Title>
      <Descriptions column={1} bordered>
        <Descriptions.Item label="Email">{user.email || '-'}</Descriptions.Item>
        <Descriptions.Item label="Username">{user.username || '-'}</Descriptions.Item>
        <Descriptions.Item label="Phone">{user.phoneNumber || '-'}</Descriptions.Item>
        <Descriptions.Item label="Roles">{user.roles?.join(', ') || '-'}</Descriptions.Item>
        <Descriptions.Item label="Status">{user.status || '-'}</Descriptions.Item>
        <Descriptions.Item label="Workspace">
          {user.workspace ? (
            <a onClick={() => window.location.href = `/workspaces/${user.workspace?.id}`}>
              {user.workspace?.name || user.workspace?.id}
            </a>
          ) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Language">{user.language || '-'}</Descriptions.Item>
        <Descriptions.Item label="Currency">{user.currency || '-'}</Descriptions.Item>
        <Descriptions.Item label="Created At">
          {user.createdAt ? (
            <Tooltip title={formatDateTimeWithTooltip(user.createdAt).tooltip}>
              <span>{formatDateTimeWithTooltip(user.createdAt).display}</span>
            </Tooltip>
          ) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Last Login">
          {user.lastLogin ? (
            <Tooltip title={formatDateTimeWithTooltip(user.lastLogin).tooltip}>
              <span>{formatDateTimeWithTooltip(user.lastLogin).display}</span>
            </Tooltip>
          ) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Invited">
          {user.invited !== undefined ? (user.invited ? 'Yes' : 'No') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Accepted">
          {user.accepted !== undefined ? (user.accepted ? 'Yes' : 'No') : '-'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

export default UserInfoTab;
